import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { getPerfil } from "../services";
import { Perfil } from "../types";
import { AUTH_CHANGED_EVENT, getStoredToken } from "../services/authSession";

interface AuthContextValue {
  perfil: Perfil | null;
  loading: boolean;
  perfilStatus: number | null;
  isAuthenticated: boolean;
  refreshPerfil: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [perfil, setPerfil] = useState<Perfil | null>(null);
  const [loading, setLoading] = useState(true);
  const [perfilStatus, setPerfilStatus] = useState<number | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const refreshPerfil = useCallback(async () => {
    const token = getStoredToken();

    if (!token) {
      setPerfil(null);
      setPerfilStatus(null);
      setIsAuthenticated(false);
      setLoading(false);
      localStorage.removeItem("perfil");
      return;
    }

    setLoading(true);
    setIsAuthenticated(true);

    try {
      const res = await getPerfil();
      setPerfil(res.data);
      setPerfilStatus(200);
      localStorage.setItem("perfil", JSON.stringify(res.data));
    } catch (err: unknown) {
      const errObj = err as { response?: { status?: number } };
      const status = errObj?.response?.status ?? null;
      setPerfil(null);
      setPerfilStatus(status);
      localStorage.removeItem("perfil");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshPerfil();
    const onAuthChanged = () => refreshPerfil();
    window.addEventListener(AUTH_CHANGED_EVENT, onAuthChanged);
    return () => window.removeEventListener(AUTH_CHANGED_EVENT, onAuthChanged);
  }, [refreshPerfil]);

  return (
    <AuthContext.Provider
      value={{ perfil, loading, perfilStatus, isAuthenticated, refreshPerfil }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx) return ctx;

  return {
    perfil: null,
    loading: true,
    perfilStatus: null,
    isAuthenticated: false,
    refreshPerfil: async () => {},
  };
}
